import { useState } from 'react';

import { useDebounce } from '~hooks/useDebounce';
import usePlaceList from '~queries/transportation/pointToPointTicket/usePlaceList';

import type { SearchPlaceDetail } from '~models/Transportation/PointToPointTicket';

interface Props {
  initialKeyword?: string;
  onSelectPlace: (place: SearchPlaceDetail) => void;
}

const usePlaceSearch = ({ initialKeyword = '', onSelectPlace }: Props) => {
  const [query, setQuery] = useState(initialKeyword);
  const [debouncedQuery, setDebouncedQuery] = useState(initialKeyword);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const debouncedSetQuery = useDebounce((value: string) => {
    setDebouncedQuery(value);
  }, 300);

  const { data, isLoading, isFetching, isFetched, isSuccess, fetchNextPage } = usePlaceList({
    keyword: debouncedQuery,
  });
  const placeList = data ? data.pages.flatMap((page) => page.content) : [];

  const isResultNotFound =
    query !== '' &&
    debouncedQuery !== '' &&
    !isLoading &&
    !isFetching &&
    isSuccess &&
    placeList.length === 0;

  const handleChangeInput = (value: string) => {
    setQuery(value);
    debouncedSetQuery(value);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    if (!placeList || placeList.length === 0 || isLoading || isFetching) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < placeList.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        if (selectedIndex >= 0 && placeList[selectedIndex]) {
          onSelectPlace(placeList[selectedIndex]);
        }
        break;
    }
  };

  return {
    query,
    debouncedQuery,
    selectedIndex,
    placeList,
    isLoading,
    isFetching,
    isFetched,
    isSuccess,
    isResultNotFound,
    handleChangeInput,
    handleKeyDown,
    fetchNextPage,
  };
};

export default usePlaceSearch;
